import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, FolderOpen, Paperclip } from "lucide-react";
import { WorkspaceDialog } from "./ClassificationWorkspaceNav";
import {
  canUseWorkspaceApi,
  ensureWorkspace,
  listWorkspaces,
  updateWorkspace,
} from "./classificationWorkspaceApi";
import { fetchReportHistory, isLoggedIn } from "./reportHistoryApi";

const STATUS = {
  reviewing: "검토 중",
  conditional: "추가 확인 필요",
  confirmed: "확정",
};

const normalizeProduct = (value) => String(value || "").trim().toLocaleLowerCase().replace(/\s+/g, " ");

const formatHsk = (raw) => {
  const code = String(raw || "").replace(/\D/g, "");
  if (code.length >= 10) return `${code.slice(0, 4)}.${code.slice(4, 6)}-${code.slice(6, 10)}`;
  if (code.length >= 6) return `${code.slice(0, 4)}.${code.slice(4, 6)}`;
  return code || "미확정";
};

const formatWhen = (value) => String(value || "").slice(0, 10) || "-";

export default function MyReportsPage() {
  const [workspaces, setWorkspaces] = useState(null);
  const [reports, setReports] = useState([]);
  const [dialogId, setDialogId] = useState(null);
  const [busyKey, setBusyKey] = useState("");
  const [error, setError] = useState("");
  const loggedIn = isLoggedIn() && canUseWorkspaceApi();

  const reload = useCallback(async () => {
    if (!loggedIn) {
      setWorkspaces([]);
      setReports([]);
      return;
    }
    try {
      setError("");
      const [workspaceResult, reportResult] = await Promise.all([
        listWorkspaces(),
        fetchReportHistory(),
      ]);
      setWorkspaces(workspaceResult.items || []);
      setReports(reportResult || []);
    } catch (e) {
      setError(e.message || "품목 관리 목록을 불러오지 못했습니다.");
      setWorkspaces([]);
    }
  }, [loggedIn]);

  useEffect(() => { reload(); }, [reload]);

  const rows = useMemo(() => {
    if (!workspaces) return [];
    const reportsByName = new Map();
    reports.forEach((report) => {
      const key = normalizeProduct(report.product_name);
      if (!key) return;
      if (!reportsByName.has(key)) reportsByName.set(key, []);
      reportsByName.get(key).push(report);
    });

    const consumed = new Set();
    const result = workspaces
      .filter((item) => !item.deletedAt && !item.archived)
      .filter((item) => {
        const names = [normalizeProduct(item.title), normalizeProduct(item.queryText)];
        return item.managed || names.some((name) => reportsByName.has(name));
      })
      .map((item) => {
        const names = [normalizeProduct(item.title), normalizeProduct(item.queryText)].filter(Boolean);
        const itemReports = [];
        names.forEach((name) => {
          (reportsByName.get(name) || []).forEach((report) => {
            if (!itemReports.some((value) => value.report_id === report.report_id)) itemReports.push(report);
          });
          consumed.add(name);
        });
        return { ...item, reports: itemReports };
      });

    reportsByName.forEach((itemReports, key) => {
      if (consumed.has(key)) return;
      const latest = itemReports[0] || {};
      result.push({
        workspaceId: null,
        title: latest.product_name || "이름 없는 품목",
        queryText: latest.product_name || "",
        recommendedHsk: latest.decided_hsk || "",
        status: "reviewing",
        managed: false,
        noteCount: 0,
        evidenceCount: 0,
        updatedAt: latest.updated_at || latest.created_at,
        reports: itemReports,
      });
    });

    return result.sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")));
  }, [reports, workspaces]);

  const openItem = async (item) => {
    const key = item.workspaceId || normalizeProduct(item.title);
    setBusyKey(key);
    setError("");
    try {
      let workspaceId = item.workspaceId;
      if (!workspaceId) {
        const created = await ensureWorkspace(item.queryText || item.title, "auto", { title: item.title });
        workspaceId = created.workspaceId;
      }
      await updateWorkspace(workspaceId, {
        managed: true,
        recommended_hsk: item.recommendedHsk || undefined,
      });
      setDialogId(workspaceId);
      await reload();
    } catch (e) {
      setError(e.message || "품목 관리 화면을 열지 못했습니다.");
    } finally {
      setBusyKey("");
    }
  };

  return (
    <div className="audit-fade-in mx-auto max-w-[1040px] px-2 py-5 text-[13px] text-gray-800">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-gray-950">품목 관리</h1>
          <p className="mt-1 text-[12px] text-gray-500">
            분류 결론, 확인 메모, 근거 자료와 첨부파일, 발급 보고서를 품목별로 한곳에 보관합니다.
          </p>
        </div>
        <div className="rounded-full bg-gray-100 px-3 py-1 text-[11px] font-medium text-gray-600">
          관리 품목 {rows.length}건
        </div>
      </div>

      {error && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-red-700">{error}</div>}
      {!loggedIn ? (
        <Empty>로그인하면 관리 품목과 첨부파일을 안전하게 보관할 수 있습니다.</Empty>
      ) : workspaces === null ? (
        <Empty>품목 관리 목록을 불러오는 중입니다.</Empty>
      ) : rows.length === 0 ? (
        <Empty>
          아직 관리 중인 품목이 없습니다. 검색 결과의 더보기 메뉴에서 <b>관리 품목으로 저장</b>을 선택하거나
          검토보고서를 발급하면 이곳에서 이어서 관리할 수 있습니다.
        </Empty>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="hidden grid-cols-[minmax(190px,1fr)_150px_120px_130px_105px] gap-3 border-b border-gray-200 bg-gray-50 px-5 py-3 text-[11px] font-semibold text-gray-500 md:grid">
            <span>품목</span><span>현재 세번</span><span>상태</span><span>보관 자료</span><span>최근 수정</span>
          </div>
          {rows.map((item) => {
            const rowKey = item.workspaceId || normalizeProduct(item.title);
            const attachmentCount = Number(item.evidenceCount || 0);
            const reportCount = item.reports?.length || 0;
            return (
              <button
                key={rowKey}
                type="button"
                disabled={busyKey === rowKey}
                onClick={() => openItem(item)}
                className="grid w-full gap-2 border-b border-gray-100 px-5 py-4 text-left transition-colors last:border-0 hover:bg-gray-50 disabled:opacity-50 md:grid-cols-[minmax(190px,1fr)_150px_120px_130px_105px] md:items-center md:gap-3"
              >
                <span className="min-w-0">
                  <span className="block truncate font-semibold text-gray-900">{item.title}</span>
                  <span className="mt-0.5 block truncate text-[11px] text-gray-400">{item.queryText}</span>
                </span>
                <span className="font-mono text-[13px] font-semibold text-blue-700">{formatHsk(item.recommendedHsk)}</span>
                <span><span className="rounded-full bg-gray-100 px-2 py-1 text-[11px] font-medium text-gray-600">{STATUS[item.status] || STATUS.reviewing}</span></span>
                <span className="flex items-center gap-3 text-[11px] text-gray-500">
                  <span className="inline-flex items-center gap-1" title="메모·근거·첨부"><Paperclip size={13} />{attachmentCount + Number(item.noteCount || 0)}</span>
                  <span className="inline-flex items-center gap-1" title="발급 보고서"><FileText size={13} />{reportCount}</span>
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] text-gray-400"><FolderOpen size={13} />{formatWhen(item.updatedAt)}</span>
              </button>
            );
          })}
        </div>
      )}

      {dialogId && (
        <WorkspaceDialog
          workspaceId={dialogId}
          onClose={() => setDialogId(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
}

function Empty({ children }) {
  return <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center leading-6 text-gray-500">{children}</div>;
}
